import { useIntl } from 'react-intl';

// MUI
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MoreVertIcon from '@mui/icons-material/MoreVert';

// screen
import { DayTitleUppercase, TemplateSettings } from 'modules/map/components/daily-dish/types';
import { getFontFamily, getEmoji } from 'modules/map/components/daily-dish/templates/utils';
import { WeekdayTitleStyle } from 'modules/map/components/daily-dish/constants';

interface WeekdayTitleProps {
  weekDay: 2 | 3 | 4 | 5 | 6;
  settings: TemplateSettings;
  /**
   * Điều chỉnh chiều dài đối với layout 1 và 2
   */
  layoutId?: 1 | 2; // 1: Two Columns, 2: Six Columns
}

/**
 * Tiêu đề ngày trong tuần
 */
const WeekdayTitle = ({ weekDay, settings, layoutId }: WeekdayTitleProps) => {
  const intl = useIntl();

  const isPinned = settings.menu.weekdayTitleStyle === WeekdayTitleStyle.PINNED;
  const shouldShowEmoji = settings.menu.emojiEnabled;
  const cardColor = settings.menu.weekday[weekDay].borderColor;

  // Function to darken a color by a percentage
  const darkenColor = (color: string): string => {
    // convert hex to rgb
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    //  range: 10-40
    const darkValue = 15;

    const darker = (value: number) => Math.max(0, Math.floor(value * (1 - darkValue / 100)));

    const red = `${darker(r).toString(16).padStart(2, '0')}`;
    const green = `${darker(g).toString(16).padStart(2, '0')}`;
    const blue = `${darker(b).toString(16).padStart(2, '0')}`;
    return `#${red}${green}${blue}`;
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          position: 'relative',
          backgroundColor: settings.menu.weekday[weekDay].borderColor,
          color: settings.font.weekdayTitle.fontColor,
          justifyContent: 'center',
          alignItems: 'center',
          height: '44px',
          width: layoutId === 2 ? '200px' : '280px',
        }}
      >
        {isPinned && (
          <MoreVertIcon
            sx={{
              position: 'absolute',
              left: 0,
              height: '100%',
              backgroundColor: darkenColor(cardColor),
              fontSize: '24px',
            }}
          />
        )}
        <Typography
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            marginLeft: isPinned ? '24px' : 0,
            marginRight: shouldShowEmoji ? '12px' : 0, // tránh bị đè bởi icon
            fontSize: `${settings.font.weekdayTitle.fontSize}px`,
            color: settings.font.weekdayTitle.fontColor,
            fontFamily: getFontFamily(settings.font.weekdayTitle.fontFamilyId),
          }}
        >
          {DayTitleUppercase(intl)[weekDay]}
        </Typography>
        {shouldShowEmoji &&
          (() => {
            const emoji = getEmoji(settings.menu.weekday[weekDay].emojiId);

            return emoji ? (
              <Box
                sx={{
                  position: 'absolute',
                  right: -12,
                  borderRadius: '50%',
                  overflow: 'hidden',
                  backgroundColor: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '4px',
                  boxShadow: '4px 4px 4px rgba(0,0,0,0.2)',
                }}
              >
                <Typography sx={{ fontSize: '24px' }}>{emoji}</Typography>
              </Box>
            ) : null;
          })()}
      </Box>
    </>
  );
};

export default WeekdayTitle;
